// ─────────────────────────────────────────────────────────────────────────────
// SITE CONFIG — all marketing copy lives here. Edit this file to change the site.
// (Hero + contact details can also be edited live from /admin — those edits
// override the values below without a redeploy.)
// ─────────────────────────────────────────────────────────────────────────────

export const SITE = {
    brand: {
        name: "Bangde Construction LLP",
        short: "Bangde",
        tagline: "Revenue software that fits how your team actually sells.",
    },

    contact: {
        email: "shailesh@bangdeconstruction.in",
        phone: "+91 98191 96244",
        whatsapp: "919819196244", // digits only, with country code — used for the wa.me chat link
        linkedin: "https://www.linkedin.com/in/bangde-constructions-llp-4454583b6",
    },

    nav: [
        { label: "Solutions", href: "#solutions" },
        { label: "How It Works", href: "#how-it-works" },
        { label: "Case Studies", href: "#case-studies" },
        { label: "About", href: "#about" },
        { label: "Contact", href: "#contact" },
    ],

    hero: {
        eyebrow: "Revenue Engineering Studio · Mumbai, India",
        headline: "Stop losing deals to slow follow-up.",
        subhead:
            "Revenue software that fits how your team actually sells — custom CRM, sales automation and pipeline visibility for B2B teams of 10 to 500.",
        primaryCta: "Book a Free Discovery Call",
        secondaryCta: "See How It Works",
        microTrust: ["Fixed-scope builds", "You own the code", "Support after launch"],
    },

    trust: {
        label: "Trusted by revenue teams at",
        logos: ["NorthPeak Infra", "Vantage Works", "Corebridge", "Meridian & Co", "BuildRight AEC"],
        stats: [
            { value: "40%", label: "faster follow-up" },
            { value: "2x", label: "pipeline visibility" },
            { value: "10+ hrs", label: "saved per rep weekly" },
        ],
    },

    problems: {
        heading: "Sound familiar?",
        sub: "Most B2B teams do not have a selling problem. They have a systems problem.",
        items: [
            {
                icon: "Filter",
                title: "Leads slip through the cracks",
                text: "Enquiries land in inboxes, WhatsApp threads and spreadsheets. Half of them never get a first response — and nobody notices until the quarter ends.",
            },
            {
                icon: "AlarmClock",
                title: "Follow-ups depend on memory",
                text: "Your best reps follow up religiously. Everyone else follows up when they remember. Deals stall not on price, but on silence.",
            },
            {
                icon: "Layers",
                title: "No single source of truth",
                text: "Sales has one number, finance has another, and the founder has a gut feel. Without one pipeline view, every forecast is a guess.",
            },
        ],
    },

    solutions: {
        heading: "What we build",
        sub: "One engagement, one system — assembled from the blocks your revenue motion needs.",
        items: [
            {
                icon: "SquareKanban",
                title: "Custom CRM & pipeline software",
                text: "A pipeline shaped around your stages, your fields, your approval rules — not a generic tool your team works around.",
            },
            {
                icon: "UserSearch",
                title: "Lead sourcing & enrichment automation",
                text: "New leads captured, deduplicated and enriched automatically, so reps start conversations instead of data entry.",
            },
            {
                icon: "Send",
                title: "Outreach & follow-up automation",
                text: "Sequences, reminders and nudges that fire on time, every time — across email, WhatsApp and calendar.",
            },
            {
                icon: "BarChart3",
                title: "Revenue dashboards & reporting",
                text: "Live pipeline value, conversion rates and rep activity in one view leadership actually opens every morning.",
            },
            {
                icon: "Blocks",
                title: "Integrations",
                text: "Email, calendar, Apollo, HubSpot, Salesforce, accounting tools — connected into one flow, with no swivel-chair copy-paste.",
            },
            {
                icon: "Bot",
                title: "AI assistants for sales workflows",
                text: "Call summaries, next-step suggestions and lead scoring drafted by AI, reviewed and sent by your reps.",
            },
        ],
    },

    steps: {
        heading: "How it works",
        sub: "A fixed-scope build, not an endless retainer. You see progress every week.",
        items: [
            {
                step: "01",
                title: "Discover",
                text: "We shadow your sales motion for a week and map exactly where revenue leaks.",
            },
            {
                step: "02",
                title: "Design",
                text: "You get a clickable blueprint of your future system before a line of code is written.",
            },
            {
                step: "03",
                title: "Build",
                text: "Weekly demos, real data, your reps testing from week two — no big-bang reveals.",
            },
            {
                step: "04",
                title: "Optimize",
                text: "Post-launch, we tune automation and dashboards against the numbers that move.",
            },
        ],
    },

    cases: {
        heading: "Results we aim to repeat",
        sub: "Sample engagements shown for illustration — client details available on a call.",
        items: [
            {
                tag: "SAMPLE",
                company: "Regional building-materials distributor",
                size: "85 employees",
                challenge: "Quotes sent by WhatsApp, follow-ups untracked, 30% of enquiries unanswered.",
                solution: "Custom CRM with quote tracking and automated follow-up sequences on email + WhatsApp.",
                result: "Response time cut from 2 days to under 2 hours.",
                metric: "40%",
                metricLabel: "faster follow-up",
            },
            {
                tag: "SAMPLE",
                company: "B2B professional services firm",
                size: "40 employees",
                challenge: "Pipeline lived in the founder's head; monthly forecasting was guesswork.",
                solution: "Pipeline visibility dashboard with stage-wise conversion and aging-deal alerts.",
                result: "Leadership reviews moved from monthly spreadsheets to a live Monday dashboard.",
                metric: "2x",
                metricLabel: "pipeline visibility",
            },
            {
                tag: "SAMPLE",
                company: "Industrial equipment reseller",
                size: "120 employees",
                challenge: "Reps spent a day a week on manual data entry across 4 disconnected tools.",
                solution: "Lead enrichment automation plus two-way sync between CRM, email and accounting.",
                result: "Data entry eliminated; reps reinvested the time into active deals.",
                metric: "10+ hrs",
                metricLabel: "saved per rep weekly",
            },
        ],
    },

    why: {
        heading: "Why Bangde",
        sub: "A small senior team that builds around your process — and stays after launch.",
        points: [
            {
                icon: "Workflow",
                title: "Built around your sales process",
                text: "We adapt the software to how your team sells today — not the other way around.",
            },
            {
                icon: "Users",
                title: "Senior engineers only",
                text: "The people on your discovery call are the people who build your system.",
            },
            {
                icon: "Code2",
                title: "You own the code",
                text: "Full source code, documentation and admin access. No lock-in, ever.",
            },
            {
                icon: "ShieldCheck",
                title: "Support after launch",
                text: "Optional monthly support keeps automations and integrations healthy as you scale.",
            },
        ],
        founder: {
            quote: "Most revenue tools fail because they ask teams to change how they sell. We build the tool around the team instead — that is the whole philosophy.",
            name: "Liliia Bangde",
            role: "CEO, Bangde Construction LLP · Mumbai",
            photo: "/ceo.jpg", // CEO headshot — swap by replacing public/ceo.jpg or pasting a URL in Admin Studio
        },
    },

    faq: {
        heading: "Questions, answered",
        items: [
            {
                q: "How long does a typical build take?",
                a: "Most revenue systems ship in 6 to 10 weeks. You see a working slice with your real data by week two, and we agree the full timeline in writing before work starts.",
            },
            {
                q: "How do you price projects?",
                a: "Fixed-scope, fixed-price. After the discovery phase you get one number for the whole build — no hourly billing, no surprise invoices. Optional monthly support is priced separately.",
            },
            {
                q: "Can you integrate with the tools we already use?",
                a: "Yes. Email and calendar (Gmail/Outlook), Apollo, HubSpot, Salesforce, WhatsApp Business, payment gateways and most accounting tools. If it has an API or a webhook, we can connect it.",
            },
            {
                q: "How is our data handled and secured?",
                a: "Your data lives in your own cloud or a dedicated database, encrypted in transit and at rest, with role-based access for your team. We sign NDAs and data-processing terms as standard.",
            },
            {
                q: "What happens after launch?",
                a: "Every build includes a stabilization period. After that, most clients keep a light monthly support plan covering monitoring, tweaks and small enhancements — but it is optional, not a lock-in.",
            },
            {
                q: "Who owns the code and the system?",
                a: "You do. Full source code, deployment access and documentation are handed over at launch. The site and systems stay yours to modify anytime, anywhere.",
            },
        ],
    },

    finalCta: {
        heading: "See what a pipeline built for your team looks like.",
        sub: "A free 30-minute discovery call. We map your current sales motion and tell you honestly whether custom software is worth it for you.",
        teamSizes: ["1–10", "11–50", "51–200", "201–500", "500+"],
    },
};
